import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import '../styles/LoanAcceptance.css';

function LoanAcceptance({ loan, currentUser, onComplete }) {
  const [signature, setSignature] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionNotes, setRejectionNotes] = useState('');

  async function handleAccept() {
    if (!signature.trim()) {
      setError('Please type your full name as signature');
      return;
    }

    if (!agreed) {
      setError('Please check the agreement box to proceed');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const timestamp = new Date().toISOString();

      await updateDoc(doc(db, 'loans', loan.id), {
        status: 'active',
        acceptedAt: timestamp,
        loaneeSignature: signature,
        loaneeSignedAt: timestamp,
        loaneeIpAddress: 'N/A', // Could be captured if needed
        agreementAccepted: true
      });

      if (onComplete) onComplete();
    } catch (err) {
      setError('Failed to accept loan');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  function handleRejectClick() {
    setShowRejectModal(true);
  }

  async function handleRejectConfirm() {
    if (!rejectionNotes.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      await updateDoc(doc(db, 'loans', loan.id), {
        status: 'rejected',
        rejectedAt: new Date().toISOString(),
        rejectionReason: rejectionNotes,
        rejectedBy: currentUser.displayName
      });
      
      if (onComplete) onComplete();
    } catch (err) {
      setError('Failed to reject loan');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="loan-acceptance-container">
      <h2>Loan Agreement</h2>
      
      <div className="loan-summary-section">
        <h3>Loan Details</h3>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="label">Loan Amount:</span>
            <span className="value">${loan.amount.toFixed(2)}</span>
          </div>
          <div className="summary-item">
            <span className="label">Interest Rate:</span>
            <span className="value">{loan.interestRate}%</span>
          </div>
          <div className="summary-item">
            <span className="label">Term:</span>
            <span className="value">{loan.termLength} {loan.termType}</span>
          </div>
          <div className="summary-item">
            <span className="label">Monthly Payment:</span>
            <span className="value">${loan.monthlyPayment.toFixed(2)}</span>
          </div>
          <div className="summary-item">
            <span className="label">Total Interest:</span>
            <span className="value">${loan.totalInterest.toFixed(2)}</span>
          </div>
          <div className="summary-item">
            <span className="label">Total Amount to Repay:</span>
            <span className="value highlight">${(loan.amount + loan.totalInterest).toFixed(2)}</span>
          </div>
          <div className="summary-item">
            <span className="label">First Payment Date:</span>
            <span className="value">{loan.startDate ? new Date(loan.startDate).toLocaleDateString() : 'N/A'}</span>
          </div>
          <div className="summary-item">
            <span className="label">Loaner:</span>
            <span className="value">{loan.loanerName}</span>
          </div>
        </div>
      </div>

      <div className="terms-section">
        <h3>Family Loan Agreement</h3>
        <div className="terms-content">
          <p><strong>Date:</strong> {new Date().toLocaleDateString()}</p>
          
          <h4>Parties:</h4>
          <p><strong>Lender:</strong> {loan.loanerName}</p>
          <p><strong>Borrower:</strong> {loan.loaneeName}</p>

          <h4>1) Loan Amount & Purpose</h4>
          <p>Lender agrees to loan Borrower <strong>${loan.amount.toFixed(2)}</strong> ("Principal") for {loan.purpose}.</p>

          <h4>2) Interest Rate</h4>
          <p>Fixed annual interest rate of <strong>{loan.interestRate}% APR</strong>, calculated on the outstanding principal balance.</p>

          <h4>3) Repayment Terms</h4>
          <p>Borrower will repay the loan in <strong>{loan.termLength} equal monthly payments</strong> as shown in the Payment Schedule below. Payments include principal and interest.</p>
          <p><strong>First payment due:</strong> {loan.startDate ? new Date(loan.startDate).toLocaleDateString() : 'TBD'}</p>
          <p>Borrower may prepay all or part at any time without penalty. Prepayments apply first to accrued interest, then to principal.</p>

          <h4>4) Method of Payment</h4>
          <p>By cash, check, bank transfer, or another mutually agreed method to the Lender.</p>

          <h4>5) Late Payments</h4>
          <p>A payment not received within 10 calendar days after its due date may be assessed a $15.00 late fee. Interest continues to accrue on unpaid principal.</p>

          <h4>6) Default</h4>
          <p>If any amount remains unpaid 30 days after its due date, the remaining unpaid principal and accrued interest may be declared immediately due and payable at the Lender's option.</p>

          <h4>7) No Assignment</h4>
          <p>Borrower may not assign this Agreement without Lender's written consent.</p>

          <h4>8) Governing Law</h4>
          <p>This Agreement is governed by the laws of the State of Idaho.</p>

          <h4>9) Entire Agreement</h4>
          <p>This document contains the entire agreement. Any changes must be in writing and signed by both parties.</p>

          {loan.notes && (
            <div className="additional-terms">
              <h4>Additional Terms:</h4>
              <p>{loan.notes}</p>
            </div>
          )}

          <h4>Payment Schedule ({loan.interestRate}% APR, {loan.termLength} payments)</h4>
          <div className="payment-schedule-summary">
            <table className="schedule-table">
              <thead>
                <tr>
                  <th>Pmt #</th>
                  <th>Due Date</th>
                  <th>Payment</th>
                  <th>Interest</th>
                  <th>Principal</th>
                  <th>Balance After</th>
                </tr>
              </thead>
              <tbody>
                {loan.amortizationSchedule?.map((row) => (
                  <tr key={row.period}>
                    <td>{row.period}</td>
                    <td>{row.paymentDate ? new Date(row.paymentDate).toLocaleDateString() : 'TBD'}</td>
                    <td>${row.payment.toFixed(2)}</td>
                    <td>${row.interest.toFixed(2)}</td>
                    <td>${row.principal.toFixed(2)}</td>
                    <td>${row.balance.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="schedule-totals">
              <p><strong>Total of Payments:</strong> ${(loan.amount + loan.totalInterest).toFixed(2)}</p>
              <p><strong>Total Interest:</strong> ${loan.totalInterest.toFixed(2)}</p>
              <p><strong>Principal:</strong> ${loan.amount.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      <div className="signature-section">
        <h3>Electronic Signature</h3>
        <p className="signature-instructions">
          By typing your full name below, you are electronically signing this loan agreement.
          This signature is legally binding and indicates your acceptance of all terms and conditions.
        </p>
        
        <div className="input-group">
          <label htmlFor="signature">Type Your Full Name</label>
          <input
            id="signature"
            type="text"
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            className="input-field signature-input"
            placeholder="John Doe"
            disabled={loading}
          />
        </div>

        <div className="agreement-checkbox">
          <label>
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              disabled={loading}
            />
            <span>
              I have read and agree to the terms and conditions of this loan agreement.
              I understand that this is a legally binding contract.
            </span>
          </label>
        </div>

        <div className="timestamp-info">
          <p><small>
            Timestamp will be recorded upon acceptance: {new Date().toLocaleString()}
          </small></p>
        </div>
      </div>

      <div className="action-buttons">
        <button
          className="btn btn-primary"
          onClick={handleAccept}
          disabled={loading || !signature.trim() || !agreed}
        >
          {loading ? 'Processing...' : 'Accept and Sign Loan Agreement'}
        </button>
        <button
          className="btn btn-danger"
          onClick={handleRejectClick}
          disabled={loading}
        >
          Reject Loan Offer
        </button>
      </div>

      {showRejectModal && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowRejectModal(false)}>×</button>
            <div className="loan-form-container">
              <h2>Reject Loan Offer</h2>
              <p style={{marginBottom: '1.5rem', color: 'var(--text-secondary)'}}>
                Please provide a reason for rejecting this loan offer. This will help the loaner understand your concerns and potentially make adjustments.
              </p>
              
              {error && (
                <div className="error-message" role="alert">
                  {error}
                </div>
              )}
              
              <div className="input-group">
                <label htmlFor="rejectionNotes">Reason for Rejection</label>
                <textarea
                  id="rejectionNotes"
                  value={rejectionNotes}
                  onChange={(e) => setRejectionNotes(e.target.value)}
                  className="textarea-field"
                  rows="6"
                  placeholder="e.g., Interest rate is too high, payment terms don't work for my budget, need longer repayment period, etc."
                  disabled={loading}
                />
              </div>
              
              <div className="action-buttons">
                <button
                  className="btn btn-danger"
                  onClick={handleRejectConfirm}
                  disabled={loading || !rejectionNotes.trim()}
                >
                  {loading ? 'Processing...' : 'Confirm Rejection'}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowRejectModal(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LoanAcceptance;
